import { useState } from 'react';

import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { tokens } from '../../theme-next';
import { buildGodFeedbackClientContext } from './godFeedbackContext';

interface FindingLikeProps {
  ruleId: string;
  severity: 'error' | 'warning' | 'info';
  message?: string | null;
  documentTypes?: string[];
  fields?: string[];
}

interface ExtractionLikeProps {
  id?: number;
  imageUrl?: string | null;
  docTypeDetected?: string | null;
  confidence?: number | null;
}

interface GodTribuneProps {
  registrationId: number;
  regPlate: string | null;
  parcours: string | null;
  /** Provenance du parcours : 'agence' (case cochée étape 1), 'v1', 'opus', 'regex'. */
  parcoursSource: string | null;
  aiConfidence: number | null;
  alertFindings: FindingLikeProps[];
  piecesFindings: FindingLikeProps[];
  extractions: ExtractionLikeProps[];
}

/**
 * Tribune GOD (god-only) : champ libre pour contester l'analyse IA. À l'envoi,
 * POST /api/cg/:id/god-feedback avec le texte + le contexte client rendu. Le
 * backend capture en plus le snapshot serveur exhaustif. Mirror du style
 * RejectCGModal (TextField multiline + bouton).
 */
export const GodTribune = ({
  registrationId,
  regPlate,
  parcours,
  parcoursSource,
  aiConfidence,
  alertFindings,
  piecesFindings,
  extractions,
}: GodTribuneProps) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    const feedbackText = text.trim();
    if (!feedbackText) {
      setError("Écris ta remarque avant d'envoyer.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const clientContext = buildGodFeedbackClientContext({
        regPlate,
        parcours,
        parcoursSource,
        aiConfidence,
        alertFindings,
        piecesFindings,
        extractions,
      });
      await makeAuthenticatedApiCall('post', `/api/cg/${registrationId}/god-feedback`, {
        feedbackText,
        clientContext,
      });
      setSuccess(true);
      setText('');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: tokens.color.white,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: `${tokens.radius['2xl']}px`,
        boxShadow: tokens.shadow.card,
        padding: '20px 22px',
      }}
    >
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.color.textMuted,
          marginBottom: '4px',
        }}
      >
        Désaccord avec l'analyse IA
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: tokens.color.textSecondary ?? tokens.color.textMuted,
          marginBottom: '12px',
          lineHeight: 1.4,
        }}
      >
        Explique pourquoi tu n'es pas d'accord. Le dossier complet (extractions, alertes, logs
        d'ingestion, pièces) est capturé automatiquement pour améliorer le module.
      </Typography>
      <TextField
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setSuccess(false);
        }}
        fullWidth
        multiline
        rows={4}
        placeholder="Ex : le contrôle technique est bien valide ici (contre-visite favorable), l'IA n'aurait pas dû le bloquer…"
        disabled={submitting}
      />
      {error && (
        <Alert severity="error" sx={{ marginTop: '10px' }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ marginTop: '10px' }}>
          Remarque enregistrée. Merci !
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={submitting || text.trim().length === 0}
          startIcon={submitting ? <CircularProgress size={16} thickness={5} color="inherit" /> : undefined}
        >
          {submitting ? 'Envoi…' : 'Envoyer ma remarque'}
        </Button>
      </Box>
    </Box>
  );
};
